import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, ChevronRight } from 'lucide-react';
import AssistantWidget from './components/assistant/AssistantWidget';
import RichText from './components/assistant/RichText';

const API = process.env.REACT_APP_BACKEND_URL;

function useAuthedJson(path) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}${path}`, { credentials: 'include' }).then(async (r) => {
      if (r.status === 401) return navigate('/login');
      setData(r.ok ? await r.json() : { error: true });
    });
  }, [path, navigate]);
  return data;
}

function Shell({ back, children }) {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200">
      <main className="max-w-3xl mx-auto px-4 py-8">
        <Link to={back.to} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-6" data-testid="help-back-link">
          <ArrowLeft className="w-4 h-4" /> {back.label}
        </Link>
        {children}
      </main>
      <AssistantWidget />
    </div>
  );
}

export function HelpCenterPage() {
  const articles = useAuthedJson('/api/help/articles');
  const faqs = useAuthedJson('/api/help/faqs');
  return (
    <Shell back={{ to: '/dashboard', label: 'Dashboard' }}>
      <h1 className="text-4xl sm:text-5xl font-black text-white mb-2" data-testid="help-center-title">Help Centre</h1>
      <p className="text-sm text-zinc-400 mb-8">Guides for every feature. Stuck? Ask Flip Buddy in the corner.</p>
      <div className="grid sm:grid-cols-2 gap-3 mb-10" data-testid="help-article-list">
        {(articles?.articles || []).map((a) => (
          <Link key={a.slug} to={`/help/${a.slug}`} className="group bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-[#00D26A] transition-colors" data-testid={`help-article-${a.slug}`}>
            <div className="flex items-center gap-2 text-white font-bold"><BookOpen className="w-4 h-4 text-[#00D26A]" />{a.title}<ChevronRight className="w-4 h-4 ml-auto text-zinc-600 group-hover:text-[#00D26A] transition-colors" /></div>
            <p className="text-xs text-zinc-400 mt-1">{a.summary}</p>
          </Link>
        ))}
      </div>
      <h2 className="text-lg font-bold text-white mb-3">Frequently asked questions</h2>
      <div className="space-y-2" data-testid="help-faq-list">
        {(faqs?.faqs || []).length === 0 && <p className="text-sm text-zinc-500">FAQs will appear here as the community asks questions.</p>}
        {(faqs?.faqs || []).map((f) => (
          <details key={f.faq_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`help-faq-${f.faq_id}`}>
            <summary className="cursor-pointer font-bold text-white">{f.question}</summary>
            <p className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap"><RichText text={f.answer} testid="faq" /></p>
          </details>
        ))}
      </div>
    </Shell>
  );
}

export function HelpArticlePage() {
  const { slug } = useParams();
  const a = useAuthedJson(`/api/help/articles/${slug}`);
  return (
    <Shell back={{ to: '/help', label: 'Help Centre' }}>
      {a?.error && <p className="text-zinc-400" data-testid="help-article-missing">Article not found.</p>}
      {a && !a.error && (
        <article data-testid="help-article-body">
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-3">{a.title}</h1>
          <p className="text-[#FFD700] text-sm mb-6">{a.summary}</p>
          <div className="space-y-4 text-zinc-300 leading-relaxed">
            {a.body.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </article>
      )}
    </Shell>
  );
}
